/**
 * Anexar arquivos ao cliente, direto do dossiê.
 * O arquivo fica com o nome original; "para que serve" é opcional e vale
 * para todos os arquivos escolhidos de uma vez.
 */
import { useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { clientService } from '@/services/client-service'
import { getErrorMessage } from '@/utils/error.utils'
import { MAX_DESCRIPTION_LENGTH } from './DocumentDescription'

interface ClientDocumentUploadDialogProps {
  clientId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploaded: () => void
}

export function ClientDocumentUploadDialog({ clientId, open, onOpenChange, onUploaded }: ClientDocumentUploadDialogProps) {
  const [files, setFiles] = useState<File[]>([])
  const [description, setDescription] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setFiles([])
    setDescription('')
    setError(null)
    onOpenChange(false)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!files.length) return
    setUploading(true)
    setError(null)
    try {
      for (const file of files) {
        await clientService.uploadDocument(clientId, file, 'Outro', description)
      }
      toast.success(files.length === 1 ? 'Documento anexado' : `${files.length} documentos anexados`)
      onUploaded()
      close()
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Não foi possível anexar o documento'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !uploading) close() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anexar documento</DialogTitle>
          <DialogDescription>O arquivo fica com o nome original.</DialogDescription>
        </DialogHeader>
        <form id="client-document-upload" onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-document-file">Arquivo</Label>
            <Input
              id="client-document-file"
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            {files.length > 1 && (
              <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 break-all">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                      className="rounded-sm text-muted-foreground hover:text-destructive"
                      aria-label={`Remover ${file.name}`}
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-document-description">
              Para que serve este arquivo? <span className="font-normal text-muted-foreground">(opcional)</span>
            </Label>
            <Input
              id="client-document-description"
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: RG e comprovante de residência"
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={uploading}>Cancelar</Button>
          <Button type="submit" form="client-document-upload" disabled={!files.length || uploading} loading={uploading}>
            {uploading ? 'Anexando…' : 'Anexar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
